import { useCreateSupplierProfileApi, useSearchAvailableSuppliersApi } from 'apis/supplier-profiles';
import { useGetCountriesApi } from 'apis/users/use-get-countries-api';
import {
  Button,
  ChakraAsyncSelect,
  ChakraSelect,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Text,
  useDisclosure,
} from 'design-system';
import { timeToIsoString } from 'pages/supplier-profiles/utils/format-cut-off-time';
import React, { useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

type TSupplierOption = {
  value: number;
  label: string;
};

type TMode = 'existing' | 'new';

type TInputs = {
  mode: TMode;
  existingSupplier: TSupplierOption | null;
  name: string;
  countryCode: string;
  email: string;
  imageUrl: string;
  cutOffTime: string;
  minimumOrder: string;
  isActive: boolean;
  isPublic: boolean;
  businessName: string;
  category: string;
  moq: string;
  delivery: string;
  paymentTerms: string;
  commissionRate: string;
  billingLegalName: string;
  billingAddress: string;
  taxRegistrationNo: string;
};

const defaultValues: TInputs = {
  mode: 'existing',
  existingSupplier: null,
  name: '',
  countryCode: '',
  email: '',
  imageUrl: '',
  cutOffTime: '',
  minimumOrder: '',
  isActive: true,
  isPublic: false,
  businessName: '',
  category: '',
  moq: '',
  delivery: '',
  paymentTerms: '',
  commissionRate: '',
  billingLegalName: '',
  billingAddress: '',
  taxRegistrationNo: '',
};

export interface ICreateSupplierWizardModalProps {
  trigger: JSX.Element;
}

export const CreateSupplierWizardModal = ({ trigger }: ICreateSupplierWizardModalProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [step, setStep] = useState<1 | 2>(1);

  const { data: countriesData } = useGetCountriesApi({ enabled: isOpen });
  const { loadOptions } = useSearchAvailableSuppliersApi();
  const { mutate: createSupplierProfile, isLoading } = useCreateSupplierProfileApi();

  const {
    control,
    register,
    handleSubmit,
    trigger: validateFields,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<TInputs>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { ...defaultValues },
  });

  const modeValue = useWatch({ control, name: 'mode' });
  const isActiveValue = useWatch({ control, name: 'isActive' });
  const isPublicValue = useWatch({ control, name: 'isPublic' });

  const countryOptions = useMemo(
    () =>
      (countriesData ?? []).map((country: any) => ({
        value: country?.code,
        label: country?.name ?? country?.code,
      })),
    [countriesData]
  );

  const handleClose = () => {
    reset({ ...defaultValues });
    setStep(1);
    onClose();
  };

  const handleNext = async () => {
    const isValid =
      modeValue === 'existing'
        ? await validateFields(['existingSupplier'])
        : await validateFields(['name', 'countryCode', 'email', 'minimumOrder']);
    if (isValid) setStep(2);
  };

  const onSubmit = (values: TInputs) => {
    createSupplierProfile(
      {
        body: {
          ...(values.mode === 'existing'
            ? { supplierId: values.existingSupplier?.value }
            : {
                supplier: {
                  name: values.name.trim(),
                  countryCode: values.countryCode,
                  isActive: values.isActive,
                  isPublic: values.isPublic,
                  ...(values.email.trim() ? { email: values.email.trim() } : {}),
                  ...(values.imageUrl.trim() ? { imageUrl: values.imageUrl.trim() } : {}),
                  ...(values.cutOffTime ? { cutOffTime: timeToIsoString(values.cutOffTime) } : {}),
                  ...(values.minimumOrder ? { minimumOrder: Number(values.minimumOrder) } : {}),
                },
              }),
          profile: {
            ...(values.businessName.trim() ? { businessName: values.businessName.trim() } : {}),
            ...(values.category.trim() ? { category: values.category.trim() } : {}),
            ...(values.moq.trim() ? { moq: values.moq.trim() } : {}),
            ...(values.delivery.trim() ? { delivery: values.delivery.trim() } : {}),
            ...(values.paymentTerms.trim() ? { paymentTerms: values.paymentTerms.trim() } : {}),
            ...(values.commissionRate ? { commissionRate: Number(values.commissionRate) } : {}),
            ...(values.billingLegalName.trim() ? { billingLegalName: values.billingLegalName.trim() } : {}),
            ...(values.billingAddress.trim() ? { billingAddress: values.billingAddress.trim() } : {}),
            ...(values.taxRegistrationNo.trim() ? { taxRegistrationNo: values.taxRegistrationNo.trim() } : {}),
          },
        },
      },
      { onSuccess: handleClose }
    );
  };

  return (
    <>
      {React.cloneElement(trigger, { onClick: onOpen })}

      <Modal isOpen={isOpen} onClose={handleClose} size={{ base: 'full', md: 'lg' }} scrollBehavior='inside'>
        <ModalOverlay />
        <ModalContent as='form' onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>{step === 1 ? 'Add Supplier — Details' : 'Add Supplier — Profile'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize='sm' color='gray.500' mb='md'>
              Step {step} of 2 —{' '}
              {step === 1
                ? modeValue === 'existing'
                  ? 'pick the supplier this profile belongs to'
                  : 'only name and country are required'
                : 'all fields are optional'}
            </Text>

            {step === 1 && (
              <Stack spacing='md'>
                <RadioGroup value={modeValue} onChange={(value) => setValue('mode', value as TMode)}>
                  <Stack direction={{ base: 'column', sm: 'row' }} spacing={{ base: 'xs', sm: 'lg' }}>
                    <Radio value='existing' colorScheme='red'>
                      Existing supplier
                    </Radio>
                    <Radio value='new' colorScheme='red'>
                      Create new supplier
                    </Radio>
                  </Stack>
                </RadioGroup>

                {modeValue === 'existing' && (
                  <Controller
                    control={control}
                    name='existingSupplier'
                    rules={{
                      validate: (value) => getValues('mode') === 'new' || !!value || 'Supplier is required',
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl isRequired isInvalid={!!error}>
                        <FormLabel>Supplier</FormLabel>
                        <ChakraAsyncSelect
                          {...field}
                          placeholder='Search suppliers...'
                          loadOptions={loadOptions}
                          isClearable
                        />
                        <FormHelperText>Only suppliers without a profile are listed.</FormHelperText>
                        <FormErrorMessage>{(error as any)?.message}</FormErrorMessage>
                      </FormControl>
                    )}
                  />
                )}

                {modeValue === 'new' && (
                  <>
                    <FormControl isRequired isInvalid={!!errors.name}>
                      <FormLabel>Supplier name</FormLabel>
                      <Input
                        placeholder='e.g. Fresh Foods LLC'
                        {...register('name', {
                          validate: (value) =>
                            getValues('mode') === 'existing' || value.trim().length > 0 || 'Supplier name is required',
                        })}
                      />
                      <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
                    </FormControl>

                    <Controller
                      control={control}
                      name='countryCode'
                      rules={{
                        validate: (value) => getValues('mode') === 'existing' || !!value || 'Country is required',
                      }}
                      render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                        <FormControl isRequired isInvalid={!!error}>
                          <FormLabel>Country</FormLabel>
                          <ChakraSelect
                            options={countryOptions}
                            value={countryOptions.find((option) => option.value === value) ?? null}
                            onChange={(option: any) => onChange(option?.value ?? '')}
                            onBlur={onBlur}
                            placeholder='Select country'
                            isClearable
                          />
                          <FormErrorMessage>{error?.message}</FormErrorMessage>
                        </FormControl>
                      )}
                    />

                    <FormControl isInvalid={!!errors.email}>
                      <FormLabel>Email</FormLabel>
                      <Input
                        type='email'
                        placeholder='e.g. orders@freshfoods.ae'
                        {...register('email', {
                          validate: (value) =>
                            getValues('mode') === 'existing' ||
                            !value.trim() ||
                            EMAIL_PATTERN.test(value.trim()) ||
                            'Enter a valid email address',
                        })}
                      />
                      <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Image URL</FormLabel>
                      <Input placeholder='https://...' {...register('imageUrl')} />
                    </FormControl>

                    <Stack
                      direction={{ base: 'column', sm: 'row' }}
                      spacing={{ base: 'xs', sm: 'md' }}
                      align={{ base: 'stretch', sm: 'flex-start' }}>
                      <FormControl>
                        <FormLabel>Cut-off time</FormLabel>
                        <Input type='time' {...register('cutOffTime')} />
                      </FormControl>

                      <FormControl isInvalid={!!errors.minimumOrder}>
                        <FormLabel>Min. order value</FormLabel>
                        <Input
                          type='number'
                          step='any'
                          min='0'
                          placeholder='e.g. 250'
                          {...register('minimumOrder', {
                            validate: (value) =>
                              getValues('mode') === 'existing' ||
                              !value ||
                              Number(value) > 0 ||
                              'Minimum order value must be greater than 0',
                          })}
                        />
                        <FormErrorMessage>{errors.minimumOrder?.message}</FormErrorMessage>
                      </FormControl>
                    </Stack>

                    <Stack direction={{ base: 'column', sm: 'row' }} spacing={{ base: 'xs', sm: 'xl' }}>
                      <FormControl display='flex' alignItems='center' minH='40px' w='auto'>
                        <Switch
                          id='wizard-is-active'
                          isChecked={!!isActiveValue}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue('isActive', e.target.checked)}
                          colorScheme='red'
                          me='sm'
                        />
                        <FormLabel htmlFor='wizard-is-active' mb='0' cursor='pointer'>
                          Active
                        </FormLabel>
                      </FormControl>

                      <FormControl display='flex' alignItems='center' minH='40px' w='auto'>
                        <Switch
                          id='wizard-is-public'
                          isChecked={!!isPublicValue}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue('isPublic', e.target.checked)}
                          colorScheme='red'
                          me='sm'
                        />
                        <FormLabel htmlFor='wizard-is-public' mb='0' cursor='pointer'>
                          Public
                        </FormLabel>
                      </FormControl>
                    </Stack>
                  </>
                )}
              </Stack>
            )}

            {step === 2 && (
              <Stack spacing='md'>
                <FormControl>
                  <FormLabel>Business name</FormLabel>
                  <Input placeholder='e.g. Fresh Foods Trading' {...register('businessName')} />
                  <FormHelperText>Defaults to the supplier name when left empty.</FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel>Category</FormLabel>
                  <Input placeholder='e.g. Produce' {...register('category')} />
                </FormControl>

                <FormControl>
                  <FormLabel>MOQ</FormLabel>
                  <Input placeholder='e.g. 100 AED' {...register('moq')} />
                </FormControl>

                <FormControl>
                  <FormLabel>Delivery</FormLabel>
                  <Input placeholder='e.g. Next day, Dubai only' {...register('delivery')} />
                </FormControl>

                <FormControl>
                  <FormLabel>Payment terms</FormLabel>
                  <Input placeholder='e.g. Net 30' {...register('paymentTerms')} />
                </FormControl>

                <FormControl>
                  <FormLabel>Billing legal name</FormLabel>
                  <Input placeholder='Printed on the invoice Bill To block' {...register('billingLegalName')} />
                </FormControl>

                <FormControl>
                  <FormLabel>Billing address</FormLabel>
                  <Input placeholder='e.g. Office 12, Al Quoz 3, Dubai' {...register('billingAddress')} />
                </FormControl>

                <FormControl>
                  <FormLabel>Tax registration no. (TRN)</FormLabel>
                  <Input placeholder='e.g. 100123456700003' {...register('taxRegistrationNo')} />
                </FormControl>

                <FormControl isInvalid={!!errors.commissionRate}>
                  <FormLabel>Commission %</FormLabel>
                  <Input
                    type='number'
                    step='0.01'
                    min='0'
                    max='100'
                    placeholder='e.g. 12.5'
                    {...register('commissionRate', {
                      validate: (value) =>
                        !value ||
                        (Number(value) >= 0 && Number(value) <= 100) ||
                        'Commission % must be between 0 and 100',
                    })}
                  />
                  <FormErrorMessage>{errors.commissionRate?.message}</FormErrorMessage>
                </FormControl>
              </Stack>
            )}
          </ModalBody>

          <ModalFooter>
            <HStack>
              <Button variant='outline' colorScheme='red' onClick={handleClose}>
                Cancel
              </Button>

              {step === 1 && (
                <Button variant='solid' colorScheme='red' onClick={handleNext}>
                  Next
                </Button>
              )}

              {step === 2 && (
                <>
                  <Button variant='ghost' onClick={() => setStep(1)}>
                    Back
                  </Button>
                  <Button variant='solid' colorScheme='red' type='submit' isLoading={isLoading}>
                    Create
                  </Button>
                </>
              )}
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
